import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from '../../config/axiosConfig';
import { API_ENDPOINTS } from '../../config/api';
import Layout from '../../components/Layout';
import Toast from '../../components/Toast';

const ContactLeadsList = () => {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState(''); // '', 'today', 'week', 'month'
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [updatingId, setUpdatingId] = useState(null);
    const [viewLead, setViewLead] = useState(null);
    const [editLead, setEditLead] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [savingEdit, setSavingEdit] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    };

    const fetchLeads = async () => {
        try {
            setLoading(true);
            const url = API_ENDPOINTS.CONTACT_LEADS_LIST(filter);
            const response = await axios.get(url);
            setLeads(response.data || []);
        } catch (err) {
            showToast(err.response?.data?.message || 'Failed to load leads', 'error');
            setLeads([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLeads();
    }, [filter]);

    const handleStatusChange = async (leadId, newStatus) => {
        setUpdatingId(leadId);
        try {
            await axios.patch(API_ENDPOINTS.CONTACT_LEAD_UPDATE_STATUS(leadId), { status: newStatus });
            setLeads((prev) => prev.map((l) => (l._id === leadId ? { ...l, status: newStatus } : l)));
            showToast('Status updated');
        } catch (err) {
            showToast(err.response?.data?.message || 'Failed to update status', 'error');
        } finally {
            setUpdatingId(null);
        }
    };

    const openView = (lead) => setViewLead(lead);
    const closeView = () => setViewLead(null);

    const openEdit = (lead) => {
        setEditLead(lead);
        setEditForm({
            restaurantName: lead.restaurantName || '',
            ownerName: lead.ownerName || '',
            email: lead.email || '',
            contactNo: lead.contactNo || '',
            city: lead.city || '',
            state: lead.state || '',
            pincode: lead.pincode || '',
            status: lead.status || 'pending'
        });
    };
    const closeEdit = () => { setEditLead(null); setEditForm({}); };
    const handleEditChange = (e) => {
        const { name, value } = e.target;
        if (name === 'contactNo') {
            const digitsOnly = value.replace(/\D/g, '');
            if (digitsOnly.length <= 12) setEditForm((prev) => ({ ...prev, [name]: digitsOnly }));
        } else setEditForm((prev) => ({ ...prev, [name]: value }));
    };
    const handleSaveEdit = async () => {
        if (!editLead) return;
        if ((editForm.contactNo || '').replace(/\D/g, '').length > 12) {
            showToast('Contact number should not exceed 12 digits', 'error');
            return;
        }
        setSavingEdit(true);
        try {
            const res = await axios.put(API_ENDPOINTS.CONTACT_LEAD_UPDATE(editLead._id), editForm);
            setLeads((prev) => prev.map((l) => (l._id === editLead._id ? res.data : l)));
            showToast('Lead updated');
            closeEdit();
        } catch (err) {
            showToast(err.response?.data?.message || 'Failed to update lead', 'error');
        } finally {
            setSavingEdit(false);
        }
    };

    const openDeleteConfirm = (lead) => setDeleteConfirm(lead);
    const closeDeleteConfirm = () => setDeleteConfirm(null);
    const handleDelete = async () => {
        if (!deleteConfirm) return;
        setDeletingId(deleteConfirm._id);
        try {
            await axios.delete(API_ENDPOINTS.CONTACT_LEAD_DELETE(deleteConfirm._id));
            setLeads((prev) => prev.filter((l) => l._id !== deleteConfirm._id));
            showToast('Lead deleted');
            closeDeleteConfirm();
        } catch (err) {
            showToast(err.response?.data?.message || 'Failed to delete lead', 'error');
        } finally {
            setDeletingId(null);
        }
    };

    const formatDate = (date) => {
        if (!date) return '—';
        return new Date(date).toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <Layout>
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 p-4 sm:p-6">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                        <div>
                            <Link to="/superadmin" className="text-blue-600 hover:text-blue-800 text-sm font-medium mb-2 inline-block">← Back to Dashboard</Link>
                            <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">Contact Us / Free Trial Leads</h1>
                            <p className="text-gray-600 mt-1">Leads from the Contact Us form. Update status when you have contacted them.</p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => setFilter('')}
                                className={`px-4 py-2 rounded-xl font-medium text-sm transition-colors ${filter === '' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`}
                            >
                                All
                            </button>
                            <button
                                onClick={() => setFilter('today')}
                                className={`px-4 py-2 rounded-xl font-medium text-sm transition-colors ${filter === 'today' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`}
                            >
                                Today
                            </button>
                            <button
                                onClick={() => setFilter('week')}
                                className={`px-4 py-2 rounded-xl font-medium text-sm transition-colors ${filter === 'week' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`}
                            >
                                This Week
                            </button>
                            <button
                                onClick={() => setFilter('month')}
                                className={`px-4 py-2 rounded-xl font-medium text-sm transition-colors ${filter === 'month' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'}`}
                            >
                                This Month
                            </button>
                        </div>
                    </div>

                    {loading ? (
                        <div className="bg-white rounded-2xl shadow p-12 text-center">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="mt-4 text-gray-600">Loading leads...</p>
                        </div>
                    ) : (
                        <div className="bg-white rounded-2xl shadow overflow-hidden">
                            <div className="overflow-x-auto min-w-0">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Restaurant</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Owner</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Email</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Contact</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">City / State / Pincode</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {leads.length === 0 ? (
                                            <tr>
                                                <td colSpan="8" className="px-4 py-12 text-center text-gray-500">
                                                    No leads found{filter ? ` for selected period` : ''}.
                                                </td>
                                            </tr>
                                        ) : (
                                            leads.map((lead) => (
                                                <tr key={lead._id} className="hover:bg-gray-50">
                                                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{formatDate(lead.createdAt)}</td>
                                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{lead.restaurantName}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-700">{lead.ownerName}</td>
                                                    <td className="px-4 py-3 text-sm text-gray-700">
                                                        {lead.email ? <a href={`mailto:${lead.email}`} className="text-blue-600 hover:underline">{lead.email}</a> : '—'}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-700">
                                                        <a href={`tel:${lead.contactNo}`} className="text-blue-600 hover:underline">{lead.contactNo}</a>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-600">{lead.city}, {lead.state}{lead.pincode ? ` - ${lead.pincode}` : ''}</td>
                                                    <td className="px-4 py-3">
                                                        <select
                                                            value={lead.status}
                                                            onChange={(e) => handleStatusChange(lead._id, e.target.value)}
                                                            disabled={updatingId === lead._id}
                                                            className={`text-sm font-medium rounded-lg px-3 py-1.5 border ${lead.status === 'done' ? 'bg-green-50 text-green-800 border-green-200' : 'bg-amber-50 text-amber-800 border-amber-200'}`}
                                                        >
                                                            <option value="pending">Pending</option>
                                                            <option value="done">Done</option>
                                                        </select>
                                                        {updatingId === lead._id && (
                                                            <span className="ml-2 text-xs text-gray-500">Updating...</span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <div className="flex flex-wrap gap-2">
                                                            <button type="button" onClick={() => openView(lead)} className="px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors">View</button>
                                                            <button type="button" onClick={() => openEdit(lead)} className="px-3 py-1.5 text-sm font-medium text-amber-600 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors">Edit</button>
                                                            <button type="button" onClick={() => openDeleteConfirm(lead)} className="px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors">Delete</button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            {/* View modal */}
            {viewLead && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={closeView}>
                    <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Lead Details</h3>
                        <dl className="space-y-2 text-sm">
                            <div><dt className="text-gray-500">Date</dt><dd className="font-medium text-gray-900">{formatDate(viewLead.createdAt)}</dd></div>
                            <div><dt className="text-gray-500">Owner</dt><dd className="font-medium text-gray-900">{viewLead.ownerName}</dd></div>
                            <div><dt className="text-gray-500">Restaurant</dt><dd className="font-medium text-gray-900">{viewLead.restaurantName}</dd></div>
                            <div><dt className="text-gray-500">Email</dt><dd className="font-medium text-gray-900">{viewLead.email || '—'}</dd></div>
                            <div><dt className="text-gray-500">Contact</dt><dd className="font-medium text-gray-900">{viewLead.contactNo}</dd></div>
                            <div><dt className="text-gray-500">City / State / Pincode</dt><dd className="font-medium text-gray-900">{viewLead.city}, {viewLead.state}{viewLead.pincode ? ` - ${viewLead.pincode}` : ''}</dd></div>
                            <div><dt className="text-gray-500">Status</dt><dd><span className={`font-medium ${viewLead.status === 'done' ? 'text-green-700' : 'text-amber-700'}`}>{viewLead.status}</span></dd></div>
                        </dl>
                        <button type="button" onClick={closeView} className="mt-6 w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-xl font-medium">Close</button>
                    </div>
                </div>
            )}
            {/* Edit modal */}
            {editLead && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 overflow-y-auto" onClick={closeEdit}>
                    <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 my-8" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Edit Lead</h3>
                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Owner Name *</label>
                                <input type="text" value={editForm.ownerName} onChange={handleEditChange} name="ownerName" required className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Restaurant Name *</label>
                                <input type="text" value={editForm.restaurantName} onChange={handleEditChange} name="restaurantName" required className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                <input type="email" value={editForm.email} onChange={handleEditChange} name="email" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Optional" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Contact No * (max 12)</label>
                                <input type="tel" value={editForm.contactNo} onChange={handleEditChange} name="contactNo" maxLength={12} required className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                                    <input type="text" value={editForm.city} onChange={handleEditChange} name="city" required className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">State *</label>
                                    <input type="text" value={editForm.state} onChange={handleEditChange} name="state" required className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
                                <input type="text" value={editForm.pincode} onChange={handleEditChange} name="pincode" maxLength={10} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Optional" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                                <select value={editForm.status} onChange={handleEditChange} name="status" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                                    <option value="pending">Pending</option>
                                    <option value="done">Done</option>
                                </select>
                            </div>
                        </div>
                        <div className="flex gap-3 mt-6">
                            <button type="button" onClick={closeEdit} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50">Cancel</button>
                            <button type="button" onClick={handleSaveEdit} disabled={savingEdit} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50">{savingEdit ? 'Saving...' : 'Save'}</button>
                        </div>
                    </div>
                </div>
            )}
            {/* Delete confirm */}
            {deleteConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={closeDeleteConfirm}>
                    <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">Delete Lead?</h3>
                        <p className="text-gray-600 text-sm mb-4">This will permanently delete the lead for <strong>{deleteConfirm.restaurantName}</strong> ({deleteConfirm.ownerName}).</p>
                        <div className="flex gap-3">
                            <button type="button" onClick={closeDeleteConfirm} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50">Cancel</button>
                            <button type="button" onClick={handleDelete} disabled={deletingId === deleteConfirm._id} className="flex-1 px-4 py-2 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 disabled:opacity-50">{deletingId === deleteConfirm._id ? 'Deleting...' : 'Delete'}</button>
                        </div>
                    </div>
                </div>
            )}
            {toast.show && <Toast message={toast.message} type={toast.type} />}
        </Layout>
    );
};

export default ContactLeadsList;
